import type { HelpLocale } from './help';

/**
 * THE DATE PICKER'S STRINGS (V0.51.1), in every language the app ships.
 *
 * `i18n/todos.ts`'s arrangement exactly, and for its reason: the chrome around these is
 * hardcoded English in dozens of components, so the translations are WRITTEN now and English is
 * RESOLVED until the chrome pass (`TRANSLATION-PLAN.md`). The one line that turns them on is
 * marked in `dateText()` below.
 *
 * THE WEEKDAY AND MONTH NAMES LIVE HERE rather than in `lib/calendar.ts`, because nothing under
 * `lib/` may hold a translation — that library computes which day sits in which cell and this
 * file says what to call it. `toLocaleDateString` is deliberately not used: it would follow the
 * BROWSER's locale rather than the app's, so a French reader on an English machine would get an
 * English calendar inside a French page, and the suite's answers would depend on the machine
 * running it.
 */
export const DATE_I18N = {
  /** Monday first — `monthGrid` lays the week out that way. */
  weekdays: { en: 'Mo Tu We Th Fr Sa Su', fr: 'Lu Ma Me Je Ve Sa Di', de: 'Mo Di Mi Do Fr Sa So' },
  months: {
    en: 'January February March April May June July August September October November December',
    fr: 'janvier février mars avril mai juin juillet août septembre octobre novembre décembre',
    de: 'Januar Februar März April Mai Juni Juli August September Oktober November Dezember',
  },

  /* ---- the field ---- */
  setDate: { en: 'Set a date', fr: 'Choisir une date', de: 'Datum wählen' },
  clearDate: { en: 'Clear the date', fr: 'Effacer la date', de: 'Datum entfernen' },
  openCalendar: { en: 'Open the calendar', fr: 'Ouvrir le calendrier', de: 'Kalender öffnen' },

  /* ---- the popover ---- */
  previousMonth: { en: 'Previous month', fr: 'Mois précédent', de: 'Voriger Monat' },
  nextMonth: { en: 'Next month', fr: 'Mois suivant', de: 'Nächster Monat' },
  calendarLabel: { en: 'Choose a date', fr: 'Choisir une date', de: 'Ein Datum wählen' },

  /* ---- the quick picks ---- */
  today: { en: 'Today', fr: 'Aujourd’hui', de: 'Heute' },
  tomorrow: { en: 'Tomorrow', fr: 'Demain', de: 'Morgen' },
  /** Not a quick pick — a due date in the past is not something to offer — but the field
   *  still has to be able to SAY it, because a row can already carry one. */
  yesterday: { en: 'Yesterday', fr: 'Hier', de: 'Gestern' },
  nextMonday: { en: 'Next Mon', fr: 'Lundi', de: 'Nächster Mo' },
  inAWeek: { en: '+1 week', fr: '+1 semaine', de: '+1 Woche' },
  inAMonth: { en: '+1 month', fr: '+1 mois', de: '+1 Monat' },
  clear: { en: 'Clear', fr: 'Effacer', de: 'Löschen' },
} as const;

export type DateKey = keyof typeof DATE_I18N;

/**
 * Read one string.
 *
 * THE ONE LINE THAT TURNS TRANSLATION ON — `i18n/todos.ts`'s, repeated. Every caller is served
 * English today whatever it passes; when the chrome pass lands, delete the `'en'` below.
 */
export function dateText(key: DateKey, _locale: string): string {
  const s = DATE_I18N[key] as Record<string, string>;
  const locale: HelpLocale = 'en';
  return s[locale] ?? s.en;
}

/** The seven weekday initials, Monday first. */
export const weekdayNames = (locale: string): string[] => dateText('weekdays', locale).split(' ');

/** The twelve month names, January first. */
export const monthNames = (locale: string): string[] => dateText('months', locale).split(' ');
